const STORAGE_PREFIX = 'reqdesk_diag_'

export interface MetadataPreferences {
  screenResolution: boolean
  deviceType: boolean
  timezone: boolean
  referrerUrl: boolean
  language: boolean
  platform: boolean
}

// Opt-in by default: the ticket form presents a single "share diagnostic info" toggle plus a
// visible list of what's included, and the user decides. The preference persists to
// localStorage for future tickets. Individual keys remain in the structure so a future host /
// admin UI can flip them independently without a storage migration.
const DEFAULT_PREFS: MetadataPreferences = {
  screenResolution: false,
  deviceType: false,
  timezone: false,
  referrerUrl: false,
  language: false,
  platform: false,
}

/** Always collected — minimal, non-sensitive */
export function collectMinimalMetadata(): Record<string, string> {
  const meta: Record<string, string> = {}
  try {
    meta.pageUrl = window.location.href
    meta.userAgent = navigator.userAgent
  } catch { /* SSR safety */ }
  return meta
}

/** Full diagnostic set — only included for opted-in fields */
export function collectDiagnosticMetadata(prefs: MetadataPreferences): Record<string, string> {
  const meta: Record<string, string> = {}
  try {
    if (prefs.screenResolution) meta.screenResolution = `${screen.width}x${screen.height}`
    if (prefs.deviceType) meta.deviceType = detectDeviceType()
    if (prefs.timezone) meta.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (prefs.referrerUrl && document.referrer) meta.referrerUrl = document.referrer
    if (prefs.language) meta.language = navigator.language
    if (prefs.platform) meta.platform = navigator.platform
  } catch { /* SSR safety */ }
  return meta
}

/** Combine minimal + opted-in diagnostic metadata */
export function collectAllMetadata(apiKey: string): Record<string, string> {
  const prefs = getMetadataPreferences(apiKey)
  return {
    ...collectMinimalMetadata(),
    ...collectDiagnosticMetadata(prefs),
  }
}

export function getMetadataPreferences(apiKey: string): MetadataPreferences {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${apiKey}`)
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) }
  } catch { /* ignore */ }
  return { ...DEFAULT_PREFS }
}

export function saveMetadataPreferences(apiKey: string, prefs: MetadataPreferences): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${apiKey}`, JSON.stringify(prefs))
  } catch { /* ignore */ }
}

export function isDiagnosticEnabled(apiKey: string): boolean {
  const prefs = getMetadataPreferences(apiKey)
  return Object.values(prefs).some(v => v)
}

function detectDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase()
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet'
  if (/mobile|iphone|ipod|android.*mobile|windows phone/i.test(ua)) return 'mobile'
  return 'desktop'
}

export const DIAGNOSTIC_FIELDS = [
  { key: 'screenResolution' as const, labelKey: 'diag.screenResolution' },
  { key: 'deviceType' as const, labelKey: 'diag.deviceType' },
  { key: 'timezone' as const, labelKey: 'diag.timezone' },
  { key: 'referrerUrl' as const, labelKey: 'diag.referrerUrl' },
  { key: 'language' as const, labelKey: 'diag.language' },
  { key: 'platform' as const, labelKey: 'diag.platform' },
] as const
